import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getLeagueById,
  getLeagueGameweekDetail,
} from "../services/leagueService";
import { getErrorMessage } from "../services/api";
import PageHeader from "../components/PageHeader";
import SectionCard from "../components/SectionCard";
import EmptyState from "../components/EmptyState";
import StatPill from "../components/StatPill";
import AlertMessage from "../components/AlertMessage";
import BackButton from "../components/BackButton";
import HistoryFixtureResultCard from "../components/HistoryFixtureResultCard";

function LeagueHistoryGameweekDetailPage({ userId }) {
  const { leagueId, gameweekId } = useParams();

  const numericLeagueId = Number(leagueId);
  const numericGameweekId = Number(gameweekId);

  const [league, setLeague] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    if (
      !Number.isInteger(numericLeagueId) ||
      numericLeagueId <= 0 ||
      !Number.isInteger(numericGameweekId) ||
      numericGameweekId <= 0
    ) {
      setLoading(false);
      setMessage("Invalid gameweek.");
      return;
    }

    const controller = new AbortController();

    async function load() {
      setLoading(true);
      setMessage("");

      try {
        const [leagueData, detailData] = await Promise.all([
          getLeagueById(numericLeagueId, { signal: controller.signal }),
          getLeagueGameweekDetail(
            numericLeagueId,
            numericGameweekId,
            userId,
            { signal: controller.signal }
          ),
        ]);

        setLeague(leagueData || null);
        setDetail(detailData || null);
      } catch (error) {
        if (error?.name === "AbortError") return;
        console.error(error);
        setMessage(getErrorMessage(error));
      } finally {
        setLoading(false);
      }
    }

    load();

    return () => controller.abort();
  }, [userId, numericLeagueId, numericGameweekId]);

  function formatDateTime(value) {
    if (!value) return "—";
    return new Date(value).toLocaleString([], {
      dateStyle: "medium",
      timeStyle: "short",
    });
  }

  if (!userId) {
    return (
      <SectionCard>
        <p className="text-slate-600">Loading user...</p>
      </SectionCard>
    );
  }

  if (
    !Number.isInteger(numericLeagueId) ||
    numericLeagueId <= 0 ||
    !Number.isInteger(numericGameweekId) ||
    numericGameweekId <= 0
  ) {
    return (
      <SectionCard>
        <p className="text-slate-600">Invalid gameweek.</p>
      </SectionCard>
    );
  }

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex flex-wrap gap-3">
          <div className="h-9 w-40 animate-pulse rounded-full bg-slate-200" />
          <div className="h-9 w-52 animate-pulse rounded-full bg-slate-200" />
        </div>
        <div className="rounded-3xl bg-gradient-to-r from-slate-900 to-slate-800 p-6 sm:p-8">
          <div className="h-3 w-24 animate-pulse rounded bg-slate-700" />
          <div className="mt-3 h-8 w-72 animate-pulse rounded bg-slate-700" />
          <div className="mt-2 h-4 w-24 animate-pulse rounded bg-slate-700" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-2xl bg-slate-50 px-4 py-4 ring-1 ring-slate-200">
              <div className="h-3 w-16 animate-pulse rounded bg-slate-200" />
              <div className="mt-2 h-6 w-24 animate-pulse rounded bg-slate-200" />
            </div>
          ))}
        </div>
        <SectionCard title="Fixture Breakdown">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-3xl bg-white p-4 sm:p-5 shadow-sm ring-1 ring-slate-200">
                <div className="mb-4 flex justify-between">
                  <div className="h-4 w-40 animate-pulse rounded bg-slate-200" />
                  <div className="h-6 w-20 animate-pulse rounded-full bg-slate-200" />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="h-16 animate-pulse rounded-2xl bg-slate-100" />
                  <div className="h-16 animate-pulse rounded-2xl bg-slate-100" />
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>
    );
  }

  const fixtures = Array.isArray(detail?.fixtures) ? detail.fixtures : [];

  const exactCount = fixtures.filter(
    (f) => (f.outcome || "").toLowerCase() === "exact"
  ).length;

  const correctCount = fixtures.filter(
    (f) => (f.outcome || "").toLowerCase() === "correct"
  ).length;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <BackButton
          to={`/leagues/${numericLeagueId}/history`}
          label="Back to History"
        />
        <BackButton
          to={`/leagues/${numericLeagueId}`}
          label="Back to League"
        />
      </div>

      <PageHeader
        eyebrow="Gameweek Detail"
        title={
          detail
            ? `${league?.name || "League"} • ${
                detail.name || `Gameweek ${detail.gameweekNumber}`
              }`
            : "Gameweek Detail"
        }
        subtitle={detail?.season || "—"}
        dark
        rightContent={
          <div className="rounded-2xl bg-white/10 px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-white">
            Status:{" "}
            <span className="font-semibold">{detail?.status || "—"}</span>
          </div>
        }
      />

      <AlertMessage message={message} type="error" />

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatPill label="Gameweek" value={detail?.gameweekNumber ?? numericGameweekId} />
        <StatPill label="Total Points" value={detail?.totalPoints != null ? `${detail.totalPoints} pts` : "—"} />
        <StatPill label="Exact Scores" value={exactCount} />
        <StatPill label="Correct Results" value={correctCount} />
      </section>

      <section className="grid gap-4 sm:grid-cols-2">
        <StatPill label="Deadline" value={formatDateTime(detail?.deadlineUtc)} />
        <StatPill label="Lock Time" value={formatDateTime(detail?.lockUtc)} />
      </section>

      {fixtures.length === 0 ? (
        <EmptyState
          title="No fixture detail found"
          description="Results for this gameweek are not available yet."
        />
      ) : (
        <SectionCard title="Fixture Breakdown">
          <div className="space-y-4">
            {fixtures.map((fixture) => (
              <HistoryFixtureResultCard
                key={fixture.fixtureId}
                fixture={fixture}
              />
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}

export default LeagueHistoryGameweekDetailPage;
